import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, X } from 'lucide-react';
import { inputClass, labelClass, thClass, tdClass, euro } from './adminUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { code: '', type: 'percent', value: '', min_amount: '', max_uses: '', active: true };

const AdminPromo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);

  const load = () =>
    axios
      .get(`${API}/admin/promo`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : r.data.promos || []))
      .catch(() => toast.error('Échec du chargement des codes promo'))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code,
      type: form.type,
      value: Number(form.value),
      active: form.active,
    };
    if (form.min_amount) payload.min_amount = Number(form.min_amount);
    if (form.max_uses) payload.max_uses = Number(form.max_uses);
    try {
      await axios.post(`${API}/admin/promo`, payload);
      toast.success('Code promo créé');
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const toggle = async (p) => {
    try {
      await axios.put(`${API}/admin/promo/${p.code}`, { active: !p.active });
      toast.success(p.active ? 'Code désactivé' : 'Code activé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try {
      await axios.delete(`${API}/admin/promo/${p.code}`);
      toast.success('Code supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="admin-promo">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl">Codes promo</h2>
        <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="btn-primary inline-flex items-center gap-2" data-testid="promo-new">
          <Plus size={16} /> Nouveau code
        </button>
      </div>

      {loading ? (
        <p className="text-foreground/40">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="text-foreground/40 py-8">Aucun code promo.</p>
      ) : (
        <div className="overflow-x-auto border border-black/10">
          <table className="w-full">
            <thead>
              <tr>
                <th className={thClass}>Code</th>
                <th className={thClass}>Remise</th>
                <th className={thClass}>Min.</th>
                <th className={thClass}>Utilisations</th>
                <th className={thClass}>Actif</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.code}>
                  <td className={`${tdClass} font-mono`}>{p.code}</td>
                  <td className={tdClass}>{p.type === 'percent' ? `${p.value} %` : euro(p.value)}</td>
                  <td className={tdClass}>{p.min_amount ? euro(p.min_amount) : '—'}</td>
                  <td className={tdClass}>{p.used_count ?? 0}{p.max_uses ? ` / ${p.max_uses}` : ''}</td>
                  <td className={tdClass}>
                    <button onClick={() => toggle(p)} className={`text-xs px-2 py-1 border ${p.active ? 'border-gold text-gold' : 'border-black/20 text-foreground/40'}`} data-testid="promo-toggle">
                      {p.active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className={`${tdClass} text-right`}>
                    <button onClick={() => remove(p)} className="text-foreground/50 hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-background border border-black/10 p-6 w-full max-w-lg space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-xl">Nouveau code promo</h3>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-foreground/50" /></button>
            </div>
            <div>
              <label className={labelClass}>Code *</label>
              <input className={inputClass} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BIENVENUE10" data-testid="promo-code" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Type</label>
                <select className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="percent" className="bg-background">Pourcentage (%)</option>
                  <option value="fixed" className="bg-background">Montant fixe (€)</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Valeur *</label>
                <input type="number" step="0.01" className={inputClass} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required data-testid="promo-value" />
              </div>
              <div>
                <label className={labelClass}>Montant minimum (€)</label>
                <input type="number" step="0.01" className={inputClass} value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Utilisations max</label>
                <input type="number" className={inputClass} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground/70">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Actif immédiatement
            </label>
            <button type="submit" className="btn-primary w-full" data-testid="promo-submit">Créer le code</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPromo;
